import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { LoadingProvider } from './contexts/LoadingContext';

// Core components (always loaded)
import Login from './components/Login';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Unauthorized from './components/Unauthorized';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded components for code splitting
const Dashboard = lazy(() => import('./components/Dashboard'));
const FeatureValues = lazy(() => import('./components/features/FeatureValues'));
const CatalogManagement = lazy(() => import('./components/catalog/CatalogManagement'));

// Product components
const ProductList = lazy(() => import('./components/products/ProductList'));
const ProductForm = lazy(() => import('./components/products/ProductForm'));
const ProductDetail = lazy(() => import('./components/products/ProductDetail'));
const ProductInventory = lazy(() => import('./components/products/ProductInventory'));
const ProductPricing = lazy(() => import('./components/products/ProductPricing'));
const ProductImages = lazy(() => import('./components/products/ProductImages'));
const ProductFeatureManagement = lazy(() => import('./components/products/ProductFeatureManagement'));
const ProductCombinationInventory = lazy(() => import('./components/products/ProductCombinationInventory'));
const ProductCombinationPricing = lazy(() => import('./components/products/ProductCombinationPricing'));
const ProductCombinationImages = lazy(() => import('./components/products/ProductCombinationImages'));
const ProductCombinationSpecifications = lazy(() => import('./components/products/ProductCombinationSpecifications'));
const ProductSpecificationManagement = lazy(() => import('./components/products/ProductSpecificationManagement'));

// Other components
const WebsiteImages = lazy(() => import('./components/settings/WebsiteImages'));
const UserList = lazy(() => import('./components/users/UserList'));
const UserDetail = lazy(() => import('./components/users/UserDetail'));
const OrderList = lazy(() => import('./components/orders/OrderList'));
const OrderDetails = lazy(() => import('./components/orders/OrderDetails'));
const SettingsManager = lazy(() => import('./components/settings/SettingsManager'));
const BusinessSettings = lazy(() => import('./components/business/BusinessSettings'));
const StoreSettings = lazy(() => import('./components/store/StoreSettings'));
const TaxSettings = lazy(() => import('./components/tax/TaxSettings'));
const PromotionsManagement = lazy(() => import('./components/promotions/PromotionsManagement'));

// Loading fallback component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <CircularProgress />
  </Box>
);

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  return (
    <ErrorBoundary showReset={true}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingProvider>
          <AuthProvider>
            <NavigationProvider>
              <Router>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            
            {/* Protected Routes with Layout */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/catalog"
              element={
                <ProtectedRoute requiredPermission="read-categories">
                  <Layout>
                    <CatalogManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/categories"
              element={<Navigate to="/catalog?tab=categories" replace />}
            />
            
            <Route
              path="/brands"
              element={<Navigate to="/catalog?tab=brands" replace />}
            />
            
            <Route
              path="/features"
              element={<Navigate to="/catalog?tab=features" replace />}
            />
            
            <Route
              path="/features/:featureId/values"
              element={
                <ProtectedRoute requiredPermission="read-products">
                  <Layout>
                    <FeatureValues />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Products Routes */}
            <Route
              path="/products"
              element={
                <ProtectedRoute requiredPermission="read-products">
                  <Layout>
                    <ProductList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/create"
              element={
                <ProtectedRoute requiredPermission="create-products">
                  <Layout>
                    <ProductForm mode="create" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:id"
              element={
                <ProtectedRoute requiredPermission="read-products">
                  <Layout>
                    <ProductDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:id/edit"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductForm mode="edit" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:id/inventory"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductInventory />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:id/pricing"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductPricing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:id/images"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductImages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:productId/features"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductFeatureManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:productId/specifications"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductSpecificationManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:productId/combinations/:combinationId/inventory"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductCombinationInventory />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:productId/combinations/:combinationId/pricing"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductCombinationPricing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:productId/combinations/:combinationId/images"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductCombinationImages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/products/:productId/combinations/:combinationId/specifications"
              element={
                <ProtectedRoute requiredPermission="update-products">
                  <Layout>
                    <ProductCombinationSpecifications />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Promotions Routes */}
            <Route
              path="/promotions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PromotionsManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Legacy redirect routes */}
            <Route
              path="/coupons"
              element={<Navigate to="/promotions?tab=coupons" replace />}
            />
            
            <Route
              path="/reward-points"
              element={<Navigate to="/promotions?tab=reward-points" replace />}
            />
            
            {/* Users Routes */}
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredPermission="read-users">
                  <Layout>
                    <UserList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/users/:id"
              element={
                <ProtectedRoute requiredPermission="read-users">
                  <Layout>
                    <UserDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Orders Routes */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute requiredPermission="read-orders">
                  <Layout>
                    <OrderList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/orders/:id"
              element={
                <ProtectedRoute requiredPermission="read-orders">
                  <Layout>
                    <OrderDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Settings Routes */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredPermission="read-settings">
                  <Layout>
                    <SettingsManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/website-images"
              element={
                <ProtectedRoute requiredPermission="read-settings">
                  <Layout>
                    <WebsiteImages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/image-settings"
              element={
                <ProtectedRoute requiredPermission="read-settings">
                  <Layout>
                    <WebsiteImages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/business-settings"
              element={
                <ProtectedRoute requiredPermission="read-settings">
                  <Layout>
                    <BusinessSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/store-settings"
              element={
                <ProtectedRoute requiredPermission="read-settings">
                  <Layout>
                    <StoreSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/tax-settings"
              element={
                <ProtectedRoute requiredPermission="read-settings">
                  <Layout>
                    <TaxSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <div>Profile Page (Coming Soon)</div>
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Suspense>
              </Router>
            </NavigationProvider>
          </AuthProvider>
        </LoadingProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
